import { useState, useEffect } from "react";
import { CommunicationType } from "../../../data/CommunicationType";
import {
    getCommunicationTypes,
    addOrUpdateCommunicationType,
    deleteCommunicationType,
} from "../../../services/communicationTypesAPI";

const useCommunicationTypes = () => {
    const [communicationTypes, setCommunicationTypes] = useState<CommunicationType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch communication types on mount
    useEffect(() => {
        fetchCommunicationTypes();
    }, []);

    /** Fetch the latest communication types from the API */
    const fetchCommunicationTypes = async () => {
        setLoading(true);
        setError(null);
        try {
            console.log("📡 Fetching communication types...");
            const data = await getCommunicationTypes();
            setCommunicationTypes(data);
            console.log("✅ Communication types fetched:", data);
        } catch (err: any) {
            console.error("❌ Error fetching communication types:", err);
            setError("Failed to fetch communication types. " + err.message);
        } finally {
            setLoading(false);
        }
    };

    /** Add a new communication type */
    const handleAdd = async (newType: CommunicationType) => {
        setLoading(true);
        setError(null);
        try {
            console.log("📡 Sending request to add communication type:", newType);
            await addOrUpdateCommunicationType(newType);

            console.log("✅ Successfully added communication type!");

            // ✅ Fetch latest list from backend
            await fetchCommunicationTypes();
        } catch (err: any) {
            console.error("❌ Error adding communication type:", err);
            setError("Failed to add communication type. " + err.message);
        } finally {
            setLoading(false);
        }
    };

    /** Update an existing communication type */
    const handleUpdate = async (updatedType: CommunicationType) => {
        setLoading(true);
        setError(null);
        try {
            console.log("📡 Sending UPDATE request:", updatedType);

            // Call API to update backend
            await addOrUpdateCommunicationType(updatedType);

            console.log("✅ API request successful!");

            // ✅ Fetch latest list from backend to sync UI
            await fetchCommunicationTypes();
        } catch (err: any) {
            console.error("❌ Error updating communication type:", err);
            setError("Failed to update communication type. " + err.message);
        } finally {
            setLoading(false);
        }
    };

    /** Delete a communication type */
    const handleDelete = async (comTypeId: string) => {
        console.log("📡 Sending DELETE request for:", comTypeId);

        setLoading(true);
        setError(null);

        try {
            await deleteCommunicationType(comTypeId);
            console.log("✅ Successfully deleted from API.");

            // ✅ Fetch latest list from backend to sync UI
            await fetchCommunicationTypes();
        } catch (err: any) {
            console.error("❌ API error:", err);
            setError("Failed to delete communication type. " + err.message);
        } finally {
            setLoading(false);
        }
    };

    return {
        communicationTypes,
        setCommunicationTypes,
        loading,
        error,
        handleAdd,
        handleUpdate,
        handleDelete,
    };
};

export default useCommunicationTypes;
